import { type Signature, Tool } from '@goat-sdk/core';
import { EVMWalletClient } from '@goat-sdk/wallet-evm';
import { API, type OrderEntity, OrderSide, OrderType } from '@orderly.network/types';
import { ethers } from 'ethers';
import { type Address, erc20Abi, type Hash } from 'viem';
import OrderlyAPIClient, {
  BuildersUsersVolumesResponse,
  OpenPositionAPIResponse,
  OrderbookSnapshot,
  type SettleRequestMessage,
  type WithdrawRequestMessage,
} from 'mode-shared/clients/OrderlyAPIClient';
import WagmiWalletClient from 'mode-shared/clients/WagmiWalletClient';
import { NETWORK_TOKENS } from 'mode-shared/constants/tokens';
import type { Logger } from 'mode-shared/types/logger';

import { ORDERLY_VAULT_ABI } from './abi/vaults.abi';
import {
  MESSAGE_TYPES,
  ON_CHAIN_VERIFYING_CONTRACT_ADDRESS,
  VAULT_CONTRACT_ADDRESS,
  ORDERLY_KEY_PREFIX,
} from './constants';
import {
  CancelAllOrderPendingParams,
  CancelOrderParams,
  ClosePositionParams,
  CreateOrderParams,
  DepositParams,
  GetBuildersLeaderboardParams,
  GetBuildersUsersVolumesParams,
  GetOrderbookSnapshotParams,
  GetOrdersParameters,
  GetPositionHistoryParams,
  GetUSDCInfoParams,
  WithdrawParams,
} from './parameters';
import type {
  CancelOrderResponse,
  CommonResponse,
  CreateOrderResponse,
  DepositResponse,
  EIP712Domain,
  PluginParameters,
  Token,
  WithdrawResponse,
} from './types';
import { LeaderBoard } from 'mode-shared/clients/OrderlyAPIClient/types/responses/BuildersLeaderboardResponse';
import { NoParameters } from 'mode-shared/tools/noParameters';
import { getFromLocalStorage } from 'mode-shared/utils/localStorage';

export default class ModeTradeService {
  // public static variables
  public static displayName = 'ModeTradeService';
  // private variables
  private readonly _client: OrderlyAPIClient;
  private readonly _logger: Logger;

  public constructor({ client, logger }: PluginParameters) {
    this._client = client;
    this._logger = logger;
  }

  /**
   * private methods
   */

  private async _address(walletClient: EVMWalletClient): Promise<string> {
    if ((walletClient as WagmiWalletClient).isWagmi) {
      return await (walletClient as WagmiWalletClient).getAddressAsync();
    }

    return walletClient.getAddress();
  }

  private _onChainDomain(chainID: number, verifyingContract: Address): EIP712Domain {
    return {
      name: 'Orderly',
      version: '1',
      chainId: chainID,
      verifyingContract,
    };
  }

  /**
   * Get or creates an ephemeral Orderly key that can be used to make short-lived requests. If the address is not registered,
   * the address will first be registered with the broker and then the key will be added.
   * If the key is not found in the local storage, a new key will be created and registered.
   * @param {string} address - The address to create a key for.
   * @param {EVMWalletClient} walletClient - The wallet client to be used to sign the EIP-712 request.
   * @param {boolean} forceCreate - Whether to force create a new key even if one exists.
   * @returns {Promise<string>} A promise that resolves to the Orderly private key in the format "ed25519:<base58_encoded>".
   * @private
   */
  private async _getOrderlyKey(address: string, walletClient: EVMWalletClient, forceCreate = false): Promise<string> {
    // Check for the key in local storage
    const localStorageKey = `orderly_mainnet_${address}`;
    const localStorageValue = getFromLocalStorage<{ accountId: string; orderlyKey: string }>(localStorageKey);
    if (localStorageValue && !forceCreate) {
      return `${ORDERLY_KEY_PREFIX}:${localStorageValue.orderlyKey}`;
    }

    return this._client.addNewKey(walletClient, address);
  }

  /**
   * public methods
   */

  @Tool({
    name: 'cancel_all_pending_orders_mode_trade',
    description:
      'Cancels all pending orders by asset symbol on Mode Trade (allowed symbols are returned by tool get_allowed_symbols_mode_trade)',
  })
  public async cancelAllPendingOrders(
    walletClient: EVMWalletClient,
    { symbol }: CancelAllOrderPendingParams
  ): Promise<CommonResponse> {
    const __logPrefix = `${ModeTradeService.displayName}#cancelAllPendingOrders`;
    const address = await this._address(walletClient);

    try {
      const status = await this._client.cancelPendingOrders(symbol, {
        address,
        orderlyKey: await this._getOrderlyKey(address, walletClient, true),
      });

      return {
        message: `Successfully cancelled all pending orders for ${symbol} with status "${status}"`,
        success: true,
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return {
        message: error instanceof Error ? error.message : 'Unknown error',
        success: false,
      };
    }
  }

  @Tool({
    name: 'cancel_order_mode_trade',
    description:
      'Cancel an order on Mode Trade by an order ID and symbol (allowed symbols are returned by tool get_allowed_symbols_mode_trade)',
  })
  public async cancelOrder(
    walletClient: EVMWalletClient,
    { order_id, symbol }: CancelOrderParams
  ): Promise<CancelOrderResponse> {
    const __logPrefix = `${ModeTradeService.displayName}#cancelOrder`;
    const address = await this._address(walletClient);

    try {
      const status = await this._client.cancelOrder(
        {
          orderID: order_id,
          symbol,
        },
        {
          address,
          orderlyKey: await this._getOrderlyKey(address, walletClient, true),
        }
      );

      return {
        order_id,
        message: `Order cancelled successfully with status "${status}"`,
        success: true,
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return {
        order_id,
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  @Tool({
    name: 'close_position_mode_trade',
    description: 'Close a position on Mode Trade',
  })
  public async closePosition(
    walletClient: EVMWalletClient,
    { symbol }: ClosePositionParams
  ): Promise<{ order_id: string; success: boolean; message: string }> {
    const __logPrefix = `${ModeTradeService.displayName}#closePosition`;
    let _error: string;

    try {
      const address = await this._address(walletClient);
      const orderlyKey = await this._getOrderlyKey(address, walletClient, true);

      const position = await this._client.position(symbol, {
        address,
        orderlyKey,
      });

      if (!position) {
        _error = `no position found with "${symbol}" on "${address}"`;

        this._logger.error(`${__logPrefix}: ${_error}`);

        throw new Error(_error);
      }

      if (position.position_qty === 0) {
        _error = `position quantity is zero with "${symbol}" on "${address}", there is no position to close`;

        this._logger.error(`${__logPrefix}: ${_error}`);

        throw new Error(_error);
      }

      this._logger.debug(`${__logPrefix}: found "${position.position_qty}" for "${symbol}" on "${address}"`);

      const orderID = await this._client.createOrder(
        {
          order_type: OrderType.MARKET,
          side: position.position_qty > 0 ? OrderSide.SELL : OrderSide.BUY,
          symbol: position.symbol,
          reduce_only: true,
          order_quantity: String(Math.abs(position.position_qty)),
        },
        {
          address,
          orderlyKey,
        }
      );

      return {
        order_id: `${orderID}`,
        success: true,
        message: 'Order created successfully',
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return {
        order_id: '',
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  @Tool({
    name: 'get_open_positions_mode_trade',
    description:
      'Get open positions on Mode Trade, including symbol, quantity, and unrealized PNL and liquidation price',
  })
  public async getOpenPositions(
    walletClient: EVMWalletClient,
    _: NoParameters
  ): Promise<OpenPositionAPIResponse | string> {
    const __logPrefix = `${ModeTradeService.displayName}#getOpenPositions`;
    const address = await this._address(walletClient);

    try {
      return await this._client.positions({
        address,
        orderlyKey: await this._getOrderlyKey(address, walletClient),
      });
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  @Tool({
    name: 'get_position_history_mode_trade',
    description: 'Get open positions on Mode Trade',
  })
  public async getPositionHistory(
    walletClient: EVMWalletClient,
    parameters: GetPositionHistoryParams
  ): Promise<Record<'rows', API.PositionHistory[]> | string> {
    const __logPrefix = `${ModeTradeService.displayName}#getOpenPositions`;
    const address = await this._address(walletClient);

    try {
      return await this._client.positionHistory(
        {
          address,
          orderlyKey: await this._getOrderlyKey(address, walletClient),
        },
        undefined,
        parameters
      );
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  @Tool({
    name: 'create_order_mode_trade',
    description: `Create an order on Mode Trade. Always ask the user for the order size or quantity.
    If the order attempt doesn't fit the requirement, maybe user hasn't enough balance or the order is too small.`,
  })
  public async createOrder(
    walletClient: EVMWalletClient,
    parameters: CreateOrderParams
  ): Promise<CreateOrderResponse | string> {
    const __logPrefix = `${ModeTradeService.displayName}#createOrder`;
    const address = await this._address(walletClient);
    let orderPrice = parameters.order_price;
    let orderQuantity = parameters.order_quantity;
    let orderAmount = parameters.order_amount ? parseFloat(parameters.order_amount) : undefined;

    if ([OrderType.MARKET, OrderType.ASK, OrderType.BID].includes(parameters.order_type)) {
      orderPrice = undefined;
    } else if (orderPrice === undefined) {
      return {
        success: false,
        message: 'Order price is required for this order type',
      };
    }

    try {
      const symbolsInfo = await this._client.availableSymbols();
      const info = symbolsInfo.find((symbol) => symbol.symbol === parameters.symbol);
      if (!info) {
        return {
          success: false,
          message: `Symbol "${parameters.symbol}" not found`,
        };
      }

      if (orderQuantity === undefined && orderAmount === undefined) {
        return {
          success: false,
          message: 'Order quantity or order amount is required',
        };
      }

      const orderlyKey = await this._getOrderlyKey(address, walletClient, true);

      if (orderQuantity === undefined && orderAmount !== undefined) {
        let price: number;
        if (parameters.order_type === OrderType.MARKET) {
          const orderbook = await this._client.orderbookSnapshot(parameters.symbol, {
            address,
            orderlyKey,
          });
          price = orderbook.bids[0].price;
        } else {
          price = parseFloat(orderPrice!);
        }
        const rawQty = orderAmount / price;
        const parts = info.base_tick.toString().split('.');
        const precision = parts[1] ? parts[1].length : 0;
        const factor = Math.pow(10, precision);
        orderQuantity = (Math.floor(rawQty * factor) / factor).toString();
        orderAmount = undefined; // order amount is not supported
      }

      const orderID = await this._client.createOrder(
        {
          ...parameters,
          order_price: orderPrice,
          order_quantity: orderQuantity,
          order_amount: orderAmount,
          ...(parameters.visible_quantity && {
            visible_quantity: parseFloat(parameters.visible_quantity),
          }),
        } as OrderEntity,
        {
          address,
          orderlyKey,
        }
      );

      return { order_id: orderID.toString(), success: true, message: 'Order created successfully' };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  @Tool({
    name: 'get_orders_mode_trade',
    description: 'Get orders by customized filters on Mode Trade',
  })
  public async getOrders(
    walletClient: EVMWalletClient,
    parameters: GetOrdersParameters
  ): Promise<(API.Order | API.AlgoOrder)[] | null> {
    const __logPrefix = `${ModeTradeService.displayName}#getOrders`;
    const address = await this._address(walletClient);

    try {
      return await this._client.getOrders(
        {
          address,
          orderlyKey: await this._getOrderlyKey(address, walletClient),
        },
        undefined,
        parameters
      );
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return null;
    }
  }

  @Tool({
    name: 'deposit_usdc_mode_trade',
    description: `Deposit USDC on Mode Trade.
      At the end of the deposit, warn the user: "your funds are being transferred it takes a few moments, check back in 1-2 minutes"`,
  })
  public async depositUSDC(walletClient: EVMWalletClient, parameters: DepositParams): Promise<DepositResponse> {
    const __logPrefix = `${ModeTradeService.displayName}#depositUSDC`;
    const address = await this._address(walletClient);
    const chain = walletClient.getChain();
    const usdcAddress = NETWORK_TOKENS[chain.id].find(({ symbol }) => symbol === 'USDC')?.contractAddress;
    const usdcAmount = BigInt(parameters.amount);
    const depositInput = {
      brokerHash: ethers.solidityPackedKeccak256(['string'], [this._client.brokerID()]) as Hash,
      tokenAmount: usdcAmount,
      tokenHash: ethers.solidityPackedKeccak256(['string'], ['USDC']) as Hash,
      accountId: this._client.accountIDFromAddress(address) as Address,
    };

    if (!usdcAddress) {
      const _error = 'unable to get usdc address';

      this._logger.error(`${__logPrefix}: ${_error}`);

      throw new Error(_error);
    }

    try {
      const allowanceRaw = await walletClient.read({
        address: usdcAddress,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address, VAULT_CONTRACT_ADDRESS],
      });
      const allowance = BigInt(allowanceRaw.value as string);

      if (allowance < usdcAmount) {
        await walletClient.sendTransaction({
          to: usdcAddress,
          abi: erc20Abi,
          functionName: 'approve',
          args: [VAULT_CONTRACT_ADDRESS, usdcAmount],
        });
      }

      const depositFeeRaw = await walletClient.read({
        address: VAULT_CONTRACT_ADDRESS,
        abi: ORDERLY_VAULT_ABI,
        functionName: 'getDepositFee',
        args: [address, depositInput],
      });

      const depositFee = BigInt(depositFeeRaw.value as string);
      const tx = await walletClient.sendTransaction({
        to: VAULT_CONTRACT_ADDRESS,
        abi: ORDERLY_VAULT_ABI,
        functionName: 'deposit',
        args: [depositInput],
        value: depositFee,
      });

      return { hash: tx.hash, chain: chain.id };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      throw new Error(`Deposit failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  @Tool({
    name: 'get_allowed_symbols_mode_trade',
    description: `Get allowed symbols for trading on Mode Trade.
    To render the result, first order by what you believe are the most popular.
    Then create a table with 2 columns: base currency and quote currency`,
  })
  public async getAllowedSymbols(_: EVMWalletClient, __: NoParameters): Promise<API.Symbol[]> {
    return await this._client.availableSymbols();
  }

  @Tool({
    name: 'get_balance_holdings_mode_trade',
    description: 'Get balance of user token holdings on Mode Trade',
  })
  public async getBalanceHoldings(walletClient: EVMWalletClient, _: NoParameters): Promise<API.Holding[]> {
    const address = await this._address(walletClient);

    return await this._client.holdings({
      address,
      orderlyKey: await this._getOrderlyKey(address, walletClient),
    });
  }

  @Tool({
    name: 'get_usdc_info_mode_trade',
    description: 'Get the info of the USDC token on Mode Trade',
  })
  public getUSDCInfo(walletClient: EVMWalletClient, parameters: GetUSDCInfoParams): Token {
    const __logPrefix = `${ModeTradeService.displayName}#getUSDCInfo`;
    const chain = walletClient.getChain();
    const token = NETWORK_TOKENS[chain.id].find(
      ({ symbol }) => symbol.toUpperCase() === parameters.symbol.toUpperCase()
    );
    let _error;

    if (!token) {
      _error = `token "${parameters.symbol}" not found`;

      this._logger.error(`${__logPrefix}: ${_error}`);

      throw new Error(_error);
    }

    return {
      decimals: token.decimals,
      symbol: token.symbol,
      name: token.symbol,
      chains: {
        [chain.id]: {
          contractAddress: token.contractAddress as Address,
        },
      },
    };
  }

  @Tool({
    name: 'withdraw_usdc_mode_trade',
    description: 'Withdraw USDC from Mode Trade',
  })
  public async withdrawUSDC(walletClient: EVMWalletClient, parameters: WithdrawParams): Promise<WithdrawResponse> {
    const __logPrefix = `${ModeTradeService.displayName}#withdrawUSDC`;
    const address = await this._address(walletClient);
    const chain = walletClient.getChain();
    const usdcAmount = BigInt(parameters.amount);
    let nonce: string;
    let signature: Signature;

    try {
      const orderlyKey = await this._getOrderlyKey(address, walletClient, true);
      const positions = await this._client.positions({
        address,
        orderlyKey,
      });
      const unsettledPnl = positions.rows.reduce((acc, position) => acc + position.unsettled_pnl, 0);

      if (unsettledPnl !== 0) {
        this._logger.debug(`${__logPrefix}: not all positions settled`);

        nonce = await this._client.settleNonce({
          address,
          orderlyKey,
        });

        this._logger.debug(`${__logPrefix}: successfully acquired settle nonce`);

        const settleRequestMessage: SettleRequestMessage = {
          brokerId: this._client.brokerID(),
          chainId: chain.id,
          chainType: 'EVM',
          settleNonce: nonce,
          timestamp: Date.now(),
        };

        this._logger.debug(`${__logPrefix}: request to sign settle data using address "${address}" on "${chain.id}"`);

        signature = await walletClient.signTypedData({
          domain: this._onChainDomain(chain.id, ON_CHAIN_VERIFYING_CONTRACT_ADDRESS),
          message: settleRequestMessage as unknown as Record<string, unknown>,
          primaryType: 'SettlePnl',
          types: MESSAGE_TYPES,
        });

        await this._client.settlePNL(
          {
            message: settleRequestMessage,
            signature: signature.signature,
            verifyingContract: ON_CHAIN_VERIFYING_CONTRACT_ADDRESS,
          },
          {
            address,
            orderlyKey,
          }
        );

        this._logger.debug(`${__logPrefix}: settled pnl for "${address}" on "${chain.id}"`);
      }

      nonce = await this._client.withdrawNonce({
        address,
        orderlyKey,
      });
      const withdrawRequestMessage: WithdrawRequestMessage = {
        brokerId: this._client.brokerID(),
        chainId: chain.id,
        chainType: 'EVM',
        receiver: address,
        token: 'USDC',
        amount: Number(usdcAmount),
        timestamp: String(Date.now()),
        withdrawNonce: nonce,
      };

      this._logger.debug(`${__logPrefix}: request to sign withdraw data using address "${address}" on "${chain.id}"`);

      signature = await walletClient.signTypedData({
        message: withdrawRequestMessage as unknown as Record<string, unknown>,
        primaryType: 'Withdraw',
        types: MESSAGE_TYPES,
        domain: this._onChainDomain(chain.id, ON_CHAIN_VERIFYING_CONTRACT_ADDRESS),
      });
      const withdrawID = await this._client.withdraw(
        {
          message: withdrawRequestMessage,
          signature: signature.signature,
          verifyingContract: ON_CHAIN_VERIFYING_CONTRACT_ADDRESS,
        },
        {
          address,
          orderlyKey,
        }
      );

      this._logger.debug(`${__logPrefix}: successfully sent withdraw request with ID "${withdrawID}"`);

      return {
        amount: usdcAmount.toString(),
        chain_id: chain.id,
        success: true,
        withdraw_id: withdrawID,
      };
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return {
        amount: usdcAmount.toString(),
        chain_id: chain.id,
        success: false,
      };
    }
  }

  @Tool({
    name: 'get_orderbook_snapshot_mode_trade',
    description: 'Get orderbook snapshot for a symbol on Mode Trade',
  })
  public async getOrderbookSnapshot(
    walletClient: EVMWalletClient,
    { symbol }: GetOrderbookSnapshotParams
  ): Promise<OrderbookSnapshot | string> {
    const __logPrefix = `${ModeTradeService.displayName}#getOrderbookSnapshot`;
    const address = await this._address(walletClient);

    try {
      return await this._client.orderbookSnapshot(symbol, {
        address,
        orderlyKey: await this._getOrderlyKey(address, walletClient),
      });
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  @Tool({
    name: 'get_users_volumes_mode_trade',
    description: 'Get users volumes on Mode Trade in USD',
  })
  public async getBuildersUsersVolumes(
    walletClient: EVMWalletClient,
    params: GetBuildersUsersVolumesParams
  ): Promise<BuildersUsersVolumesResponse | string> {
    const __logPrefix = `${ModeTradeService.displayName}#getBuildersUsersVolumes`;
    const address = await this._address(walletClient);

    try {
      return await this._client.getBuildersUsersVolumes(params, {
        address,
        orderlyKey: await this._getOrderlyKey(address, walletClient),
      });
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }

  @Tool({
    name: 'get_leaderboard_mode_trade',
    description: 'Get leaderboard on Mode Trade in USD',
  })
  public async getBuildersLeaderboard(
    walletClient: EVMWalletClient,
    params: GetBuildersLeaderboardParams
  ): Promise<LeaderBoard[] | string> {
    const __logPrefix = `${ModeTradeService.displayName}#getBuildersLeaderboard`;
    const address = await this._address(walletClient);

    try {
      return await this._client.getBuildersLeaderboard(params, {
        address,
        orderlyKey: await this._getOrderlyKey(address, walletClient),
      });
    } catch (error) {
      this._logger.error(`${__logPrefix}:`, error);

      return error instanceof Error ? error.message : 'Unknown error';
    }
  }
}
